"use client";

import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { CustomerType } from "../services/customer-service";
import CustomerTypeSelect from "./customer-type-select";
import UserService, { User, UserStatusCode } from "../../user/services/user-service";
import { normalizedString } from "../../../utils/helpers";

export interface CustomerFormValues {
  name: string;
  phone: string;
  address: string;
  email: string;
  isActive: boolean;
  type_customer: CustomerType;
  discount_percent: number;
  default_payment_method: number;
  requires_counting: number;
  sale_id?: number | null;
  /** Mã khách hàng — chỉ có khi cập nhật (tạo mới BE tự sinh). */
  code?: string;
  /** Toạ độ điểm giao nhận — null = chưa có / xoá. */
  lat: number | null;
  lng: number | null;
}

/**
 * Đọc toạ độ từ chuỗi người dùng dán vào: "10.7769, 106.7009" hoặc link Google Maps
 * (…/@10.7769,106.7009,17z…, …?q=10.7769,106.7009, …!3d10.7769!4d106.7009).
 * Trả null nếu không đọc được hoặc ngoài khoảng hợp lệ.
 */
export function parseLatLng(input: string): { lat: number; lng: number } | null {
  const text = (input || "").trim();
  if (!text) return null;
  const patterns = [
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/, // toạ độ chính xác của điểm ghim trong link Google Maps
    /[?&](?:q|query|ll|destination)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    /^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/,
  ];
  for (const re of patterns) {
    const m = re.exec(text);
    if (!m) continue;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng };
    }
  }
  return null;
}

interface Props {
  onClose: () => void;
  onSubmit: (payload: CustomerFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
  titleText?: string;
  submitText?: string;
  initialValues?: Partial<CustomerFormValues>;
}

const CustomerCreateModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  isSubmitting = false,
  titleText = "Thêm khách hàng mới",
  submitText = "Lưu",
  initialValues,
}) => {
  // ==========================
  // STATE FORM
  // ==========================
  const [form, setForm] = useState<CustomerFormValues>({
    name: initialValues?.name ?? "",
    phone: initialValues?.phone ?? "",
    address: initialValues?.address ?? "",
    email: initialValues?.email ?? "",
    isActive: initialValues?.isActive ?? true,
    type_customer: initialValues?.type_customer ?? CustomerType.ENTERPRISE,
    discount_percent: initialValues?.discount_percent ?? 0,
    default_payment_method: initialValues?.default_payment_method ?? 1,
    requires_counting: initialValues?.requires_counting ?? 0,
    sale_id: initialValues?.sale_id ?? null,
    code: initialValues?.code,
    lat: initialValues?.lat ?? null,
    lng: initialValues?.lng ?? null,
  });
  // Ô toạ độ là chữ tự do (dán "lat, lng" hoặc link Google Maps) → giữ riêng chuỗi đang gõ.
  const [locationText, setLocationText] = useState<string>(
    initialValues?.lat != null && initialValues?.lng != null ? `${initialValues.lat}, ${initialValues.lng}` : "",
  );
  const parsedLocation = parseLatLng(locationText);
  const locationInvalid = locationText.trim() !== "" && !parsedLocation;

  // Có `initialValues.code` = đang cập nhật → hiện ô mã. Định dạng khớp BE: GU (tự sinh) hoặc BM (khách cũ) + dãy
  // số. Chỉ kiểm tra khi người dùng đổi mã — khách có mã dạng khác vẫn cập nhật các trường khác bình thường.
  const showCode = initialValues?.code !== undefined;
  const normalizedCode = (form.code ?? "").trim().toUpperCase();
  const codeChanged = showCode && normalizedCode !== (initialValues?.code ?? "");
  const codeInvalid = codeChanged && !/^(GU|BM)\d+$/.test(normalizedCode);

  const [salesList, setSalesList] = useState<User[]>([]);
  const { fetch: fetchUsers } = UserService.list();

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const res = await fetchUsers({ status: UserStatusCode.ACTIVE, limit: 500 });
        if (mounted && res && (res as any).data?.list) {
          setSalesList((res as any).data.list);
        }
      } catch (err) {
        console.error("Lỗi tải danh sách NV Sale:", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const saleOptions = useMemo(() => {
    return salesList.map((u) => ({
      value: u.id,
      label: u.phone ? `${u.name} (${u.phone})` : u.name,
      phone: u.phone || "",
      user: u,
    }));
  }, [salesList]);

  const selectedSaleOption = useMemo(() => {
    if (!form.sale_id) return null;
    return saleOptions.find((o) => o.value === form.sale_id) || null;
  }, [form.sale_id, saleOptions]);

  // ==========================
  // HANDLE
  // ==========================
  const handleChange = (field: keyof CustomerFormValues, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (locationInvalid || codeInvalid) return;

    const success = await onSubmit({
      ...form,
      code: codeChanged ? normalizedCode : undefined,
      // Ô trống = xoá toạ độ; có giá trị = toạ độ đã đọc được.
      lat: parsedLocation?.lat ?? null,
      lng: parsedLocation?.lng ?? null,
      type_customer: form.type_customer ?? CustomerType.ENTERPRISE,
      discount_percent: Number(form.discount_percent) || 0,
      requires_counting: form.requires_counting ? 1 : 0,
    });

    if (success) onClose();
  };

  // ==========================
  // RENDER
  // ==========================
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-lg max-h-[90vh] flex-col rounded-xl bg-white shadow-xl">
        {/* HEADER */}
        <div className="flex shrink-0 justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-700">{titleText}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 space-y-5 overflow-y-auto p-6">
          {/* Mã khách hàng (chỉ khi cập nhật) */}
          {showCode && (
            <div>
              <label className="block text-sm font-medium text-slate-600">
                Mã khách hàng <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.code ?? ""}
                onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                maxLength={50}
                className={`mt-1 w-full rounded-lg border px-3 py-2 font-mono text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500 ${
                  codeInvalid ? "border-red-400" : ""
                }`}
                placeholder="GU0123"
              />
              {codeInvalid && (
                <p className="mt-1 text-xs text-red-600">Mã phải có dạng GU hoặc BM + dãy số (VD: GU0123, BM15278).</p>
              )}
            </div>
          )}

          {/* Họ tên */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Tên khách hàng <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Nguyễn Văn A"
              required
            />
          </div>

          {/* SĐT */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Số điện thoại <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="0901234567"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="example@email.com"
            />
          </div>

          {/* Địa chỉ */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Địa chỉ
            </label>
            <textarea
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="123 Lê Lợi, Quận 1, TP.HCM"
            />
          </div>

          {/* Toạ độ điểm giao nhận */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Toạ độ điểm giao nhận
            </label>
            <input
              type="text"
              value={locationText}
              onChange={(e) => setLocationText(e.target.value)}
              className={`mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500 ${
                locationInvalid ? "border-red-400" : ""
              }`}
              placeholder="10.7769, 106.7009 hoặc dán link Google Maps"
            />
            {locationInvalid ? (
              <p className="mt-1 text-xs text-red-600">
                Không đọc được toạ độ. Nhập dạng "vĩ độ, kinh độ" hoặc dán link Google Maps của điểm giao.
              </p>
            ) : parsedLocation ? (
              <p className="mt-1 text-xs text-slate-500">
                Vĩ độ {parsedLocation.lat}, kinh độ {parsedLocation.lng} ·{" "}
                <a
                  href={`https://www.google.com/maps?q=${parsedLocation.lat},${parsedLocation.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-green-700 hover:underline"
                >
                  Xem trên bản đồ
                </a>
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-400">
                Dùng để kiểm tra nhân viên hoàn thành đơn đúng địa chỉ khách. Để trống nếu chưa có — khi đó hệ thống
                tự dò theo địa chỉ như trước.
              </p>
            )}
          </div>

          {/* Nhân viên Sale */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Nhân viên Sale phụ trách
            </label>
            <Select
              options={saleOptions}
              value={selectedSaleOption}
              onChange={(opt: any) => handleChange("sale_id", opt ? opt.value : null)}
              isClearable
              isSearchable
              placeholder="-- Gõ tên hoặc SĐT để tìm NV Sale... --"
              noOptionsMessage={() => "Không tìm thấy NV Sale phù hợp"}
              filterOption={(option: any, rawInput: string) => {
                const input = normalizedString(rawInput || "");
                if (!input) return true;
                const label = normalizedString(option.data?.label || "");
                const phone = option.data?.phone || "";
                return label.includes(input) || phone.includes(input);
              }}
              formatOptionLabel={(option: any) => (
                <div className="flex items-center justify-between py-0.5">
                  <span className="font-medium text-slate-700">{option.user?.name}</span>
                  {option.user?.phone && (
                    <span className="text-xs text-slate-400 font-mono ml-2">
                      {option.user.phone}
                    </span>
                  )}
                </div>
              )}
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              menuPosition="fixed"
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                menu: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  minHeight: "42px",
                  borderRadius: "0.5rem",
                  borderColor: state.isFocused ? "#22c55e" : "#e2e8f0",
                  boxShadow: state.isFocused ? "0 0 0 1px #22c55e" : "none",
                  "&:hover": {
                    borderColor: state.isFocused ? "#22c55e" : "#cbd5e1",
                  },
                }),
              }}
            />
          </div>

          {/* 👇 Chiết khấu */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Chiết khấu (%)
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.discount_percent}
              onChange={(e) =>
                handleChange("discount_percent", Number(e.target.value) || 0)
              }
              className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Nhập % chiết khấu (VD: 10)"
            />
          </div>

          {/* Phương thức thanh toán mặc định */}
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Phương thức thanh toán mặc định
            </label>
            <select
              value={form.default_payment_method}
              onChange={(e) => handleChange("default_payment_method", Number(e.target.value))}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
            >
              <option value={1}>Tiền mặt</option>
              <option value={2}>Chuyển khoản (QR)</option>
              <option value={3}>Công nợ</option>
            </select>
          </div>

          {/* Yêu cầu kiểm đếm */}
          <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <input
              id="requires_counting"
              type="checkbox"
              checked={!!form.requires_counting}
              onChange={(e) => handleChange("requires_counting", e.target.checked ? 1 : 0)}
              className="mt-0.5 h-4 w-4 text-green-600 focus:ring-green-500"
            />
            <label htmlFor="requires_counting" className="text-sm text-slate-700">
              Yêu cầu kiểm đếm với khách
              <span className="block text-xs text-slate-400">
                Đơn của khách này sẽ bật kiểm đếm khi nhận &amp; khi giao (đối chiếu số lượng với khách).
              </span>
            </label>
          </div>

          {/* Loại khách hàng */}
          <div>
            <CustomerTypeSelect
              value={form.type_customer}
              onChange={(val) => handleChange("type_customer", val)}
            />
          </div>

          {/* Trạng thái */}
          <div className="flex items-center gap-2 pt-2">
            <input
              id="active"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
              className="h-4 w-4 text-green-600 focus:ring-green-500"
            />
            <label htmlFor="active" className="text-sm text-slate-700">
              Hoạt động
            </label>
          </div>

          </div>

          {/* BUTTONS - luôn hiển thị ở dưới modal */}
          <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-slate-600 hover:bg-slate-100"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isSubmitting || locationInvalid || codeInvalid}
              className="rounded-lg bg-green-600 px-5 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-60"
            >
              {isSubmitting ? "Đang lưu..." : submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerCreateModal;
